/**
 * Client telemetry — App Insights, converged with app-magic's `appInsights.ts`.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * On 2026-08-17 the app told users "Re-authenticating…" for three different
 * causes, and the only reason anybody found out was that a human reported it
 * three times in one day — while every API route returned 200. The same week a
 * deploy broke a tab someone had open, and it read as an auth failure for HOURS.
 * Neither left a trace anywhere we could look. That is what this is for: not
 * page-view analytics, but the client-side failures the server never sees.
 *
 * ── It is INERT until somebody sets the connection string ──────────────────
 *
 * There is no App Insights resource for app-purchasing today (checked
 * 2026-08-19: no component in rg-majans-agents, and purchasing-api-v1 carries no
 * APPLICATIONINSIGHTS_* setting). So this ships switched off, the same way the AI
 * endpoints ship 503 when ANTHROPIC_API_KEY is unset — complete, and doing
 * nothing until `VITE_APPINSIGHTS_CONNECTION_STRING` is set on the SWA build. No
 * Azure resource is created here: that is shared infrastructure and the decision
 * to create one is Amit's.
 *
 * ⚠ THE SDK IS LOADED DYNAMICALLY, and that is not a micro-optimisation.
 * Imported statically it is a 443 kB chunk — nearly double this app's entire
 * 237 kB entry bundle — and every reader would download it to run telemetry that
 * is currently disabled. Behind a dynamic import it costs exactly nothing while
 * the connection string is unset, and when it is set it arrives after first paint
 * instead of blocking it. app-magic imports it statically; this is a deliberate
 * divergence, and the reason is written here so it is not "fixed" back later.
 *
 * ⚠ Nothing below may throw. The two callers are error boundaries — already the
 * worst possible moment to introduce a second failure.
 */

import type { ApplicationInsights } from '@microsoft/applicationinsights-web';

const CONNECTION_STRING = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;

/** True only when a connection string was supplied at build time. Exported so a
 *  page can say "diagnostics are off" honestly rather than implying they are on. */
export const telemetryEnabled = Boolean(CONNECTION_STRING);

let client: ApplicationInsights | null = null;

/**
 * Errors that happened before the SDK finished loading.
 *
 * Without this the most valuable report of all would be the one guaranteed to be
 * lost: a chunk that 404s on first paint after a deploy throws while the SDK is
 * still in flight. Bounded at ten, because a render loop must not turn a fault
 * into unbounded memory — and ten spikes of the same error say as much as a
 * thousand.
 */
const pending: { error: unknown; where: string; url: string }[] = [];
const PENDING_MAX = 10;

function send(client_: ApplicationInsights, error: unknown, where: string, url: string): void {
  try {
    const exception = error instanceof Error ? error : new Error(String(error));
    client_.trackException({ exception, properties: { where, url } });
  } catch {
    /* Reporting a failure must never itself fail the render. */
  }
}

if (telemetryEnabled) {
  void import('@microsoft/applicationinsights-web')
    .then(({ ApplicationInsights: AI }) => {
      const ai = new AI({
        config: {
          connectionString: CONNECTION_STRING,
          /* Phase 4 gave every page a URL, so route changes are real page views
             now. Before the router this would have recorded one per session. */
          enableAutoRouteTracking: true,
          /* Never report from a dev server: local click-testing would otherwise
             fill the resource with noise indistinguishable from a real session. */
          disableTelemetry: import.meta.env.DEV,
        },
      });
      ai.loadAppInsights();
      client = ai;
      while (pending.length) {
        const p = pending.shift();
        if (p) send(ai, p.error, p.where, p.url);
      }
    })
    .catch(() => {
      /* A telemetry SDK that will not load must not take the app with it. The
         queue is dropped: holding it forever would be the memory leak this guard
         exists to avoid. */
      pending.length = 0;
    });
}

/**
 * Report an error a BOUNDARY caught.
 *
 * This is the part `loadAppInsights()` cannot do for us. Its automatic handler
 * hooks UNHANDLED errors — but a React error boundary is by definition the thing
 * that stops an error being unhandled. Both of this app's boundaries
 * (PageBoundary, GridBoundary) swallow the throw and render a message instead, so
 * without this call the exact failures worth knowing about — a page that will not
 * load, a grid whose chunk 404'd after a deploy — would be the ones that never
 * arrive.
 *
 * `where` names the boundary so a spike can be read without a stack trace.
 */
export function trackError(error: unknown, where: string): void {
  if (!telemetryEnabled) return;
  const url = typeof window === 'undefined' ? '' : window.location.pathname;
  if (client) { send(client, error, where, url); return; }
  if (pending.length < PENDING_MAX) pending.push({ error, where, url });
}
